import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import constantAdditionalInfos from './episAdditionalInfos';
import { useNavigate } from 'react-router-dom';
import { useHomeSecurity } from '@contexts/HomeSecurityContext/HomeSecurityContext';

type EpisKeys = keyof typeof constantAdditionalInfos;

export const useEPIPageVM = () => {
    const svgKeys: EpisKeys[] = useMemo(() => ['Casque', 'Lunettes', 'Casque_oreilles', 'Masque', 'Vetements', 'Gants', 'Chaussures'], []);
    const additionalInfos = useMemo(() => constantAdditionalInfos, []);

    const containerRef = useRef<HTMLDivElement>(null);
    const [activeKeys, setActiveKeys] = useState<EpisKeys[]>([]);
    const [activeAdditionalInfo, setActiveAdditionalInfo] = useState<(typeof constantAdditionalInfos)[EpisKeys]>();
    const [currentKey, setCurrentKey] = useState<EpisKeys>();
    const [isFinished, setIsFinished] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showEPISelection, setShowEPISelection] = useState(false);
    const [selectedEPIs, setSelectedEPIs] = useState<EpisKeys[]>([]);

    const [animationClass, setAnimationClass] = useState('');
    const [lastInfo, setLastInfo] = useState(activeAdditionalInfo);

    const navigate = useNavigate();
    const { handleAddPassedModule, setSelectedEPIs: setSelectedEPIsFromContext, worksiteId } = useHomeSecurity();

    useEffect(() => {
        if (!worksiteId) {
            navigate('/sante-securite/home-security');
        }
    }, [worksiteId, navigate]);

    const displayAdditionalInfo = useCallback(
        (key: EpisKeys) => {
            setActiveAdditionalInfo(additionalInfos[key]);

            const epiPage = document.querySelector('.epi-page');
            if (!epiPage) return;

            let block: ScrollLogicalPosition = 'center';

            switch (key) {
                case 'Chaussures':
                    block = 'end';
                    break;
                case 'Casque_oreilles':
                case 'Casque':
                case 'Lunettes':
                    block = 'start';
                    break;
                default:
                    block = 'center';
            }

            setTimeout(() => {
                epiPage.scrollIntoView({ behavior: 'smooth', block });
            }, 0);
        },
        [additionalInfos]
    );

    const toggleKey = (key: EpisKeys) => {
        setActiveKeys((prev) => {
            if (prev.includes(key)) {
                return prev;
            } else {
                return [...prev, key];
            }
        });
        setCurrentKey(key);
    };

    const toggleSelectedEPI = useCallback((key: EpisKeys) => {
        setSelectedEPIs((prev) => {
            if (prev.includes(key)) {
                return prev.filter((epi) => epi !== key);
            } else {
                return [...prev, key];
            }
        });
    }, []);

    const handleNext = useCallback(() => {
        if (activeKeys.length === svgKeys.length) {
            const pins = [
                document.getElementById(`Pin_${currentKey?.toLowerCase()}_couleurs`),
                document.getElementById(`Pin_${currentKey?.toLowerCase()}_gris`)
            ];

            pins.forEach((pin) => {
                if (pin) {
                    (pin as HTMLElement).classList.add('hide-svg-item');
                }
            });
            setIsFinished(true);
            setShowEPISelection(true);

            const epiPage = document.querySelector('.epi-page');
            if (!epiPage) return;

            epiPage.scrollIntoView({ behavior: 'smooth', block: 'start' });

            return;
        }

        const keysNotSeen = svgKeys.filter((key) => !activeKeys.includes(key));
        setActiveKeys([...activeKeys, keysNotSeen[0]]);
        displayAdditionalInfo(keysNotSeen[0]);
        setCurrentKey(keysNotSeen[0]);
    }, [activeKeys, svgKeys, displayAdditionalInfo, currentKey]);

    const handleFinishSelection = useCallback(() => {
        navigate('/sante-securite/home-security/menu');
        handleAddPassedModule('epis');
    }, [navigate, handleAddPassedModule]);

    useEffect(() => {
        if (!containerRef.current) return;

        svgKeys.forEach((key) => {
            const couleurElement = document.getElementById(`${key}_couleurs`);
            const pinCouleurElement = document.getElementById(`Pin_${key.toLowerCase()}_couleurs`);
            if (couleurElement) {
                (couleurElement as HTMLElement).classList.add('hide-svg-item');
            }
            if (pinCouleurElement) {
                (pinCouleurElement as HTMLElement).classList.add('hide-svg-item');
            }
        });

        activeKeys.forEach((key) => {
            const couleurElement = document.getElementById(`${key}_couleurs`);
            if (couleurElement) {
                (couleurElement as HTMLElement).classList.remove('hide-svg-item');
            }

            if (key === currentKey) {
                const pinCouleurElement = document.getElementById(`Pin_${key.toLowerCase()}_couleurs`);

                if (pinCouleurElement) {
                    (pinCouleurElement as HTMLElement).classList.remove('hide-svg-item');
                }
            } else {
                const pins = [
                    document.getElementById(`Pin_${key.toLowerCase()}_couleurs`),
                    document.getElementById(`Pin_${key.toLowerCase()}_gris`)
                ];

                pins.forEach((pin) => {
                    if (pin) {
                        (pin as HTMLElement).classList.add('hide-svg-item');
                    }
                });
            }
        });

        if (activeKeys.length === svgKeys.length) {
            const pins = [
                document.getElementById(`Pin_${currentKey?.toLowerCase()}_couleurs`),
                document.getElementById(`Pin_${currentKey?.toLowerCase()}_gris`)
            ];

            pins.forEach((pin) => {
                if (pin) {
                    (pin as HTMLElement).classList.remove('hide-svg-item');
                }
            });
        }

        svgKeys.forEach((key) => {
            const pinElement = document.getElementById(`Pin_${key.toLowerCase()}_gris`);
            if (pinElement) {
                pinElement.onclick = () => {
                    toggleKey(key);
                    displayAdditionalInfo(key);
                };
            }
        });
    }, [svgKeys, activeKeys, displayAdditionalInfo, currentKey]);

    useEffect(() => {
        if (activeAdditionalInfo !== lastInfo) {
            setAnimationClass('animate-dissolve-exit');

            const timer = setTimeout(() => {
                setLastInfo(activeAdditionalInfo);
                setAnimationClass('animate-dissolve-enter');
            }, 300);

            return () => clearTimeout(timer);
        }
    }, [activeAdditionalInfo, lastInfo]);

    useEffect(() => {
        if (isFinished && !showEPISelection) {
            setShowModal(true);

            const modalTimeout = setTimeout(() => {
                setShowModal(false);
                setShowEPISelection(true);
            }, 1500);

            return () => {
                clearTimeout(modalTimeout);
            };
        }
    }, [isFinished, showEPISelection]);

    useEffect(() => {
        setSelectedEPIsFromContext(selectedEPIs);
    }, [selectedEPIs, setSelectedEPIsFromContext]);

    return {
        containerRef,
        activeAdditionalInfo,
        handleNext,
        isFinished,
        showModal,
        setShowModal,
        animationClass,
        lastInfo,
        showEPISelection,
        selectedEPIs,
        toggleSelectedEPI,
        handleFinishSelection,
        svgKeys
    };
};
